import { EventEmitter, Input, Output } from '@angular/core';

import { browserLanguage, thfLocaleDefault } from '../../utils/util';

import { thfModalPasswordRecoveryLiterals } from './literals/i18n/thf-modal-password-recovery-literals';
import { ThfModalPasswordRecoveryType } from './enums/thf-modal-password-recovery-type.enum';

const ThfModalPasswordRecoveryDefaultMaxLength = 15;
const ThfModalPasswordRecoveryDefaultMinLength = 15;
const ThfModalPasswordRecoveryDefaultPhone = '(99) 99999-9999';
const ThfModalPasswordRecoveryTypeDefault: ThfModalPasswordRecoveryType = ThfModalPasswordRecoveryType.Email;

/**
 * @description
 *
 * O componente `thf-modal-password-recovery` Ã© utilizado como template para solicitaÃ§Ã£o de troca de senha.
 *
 * Ã composto por uma modal que possui trÃªs telas, cada uma com as seguintes caracterÃ­sticas:
 *
 * - A primeira possui campos para preenchimento de email ou nÃºmero de telefone;
 * - Tela com campo para preenchimento de cÃ³digo SMS enviado para o nÃºmero de telefone enviado;
 * - A terceira se trata de uma confirmaÃ§Ã£o de envio de link para a caixa de email do usuÃ¡rio.
 *
 *
 * A propriedade `t-url-recovery` automatiza a rotina do componente e simplifica o processo
 * para recuperaÃ§Ã£o de senha, bastando definir uma url para requisiÃ§Ã£o dos recursos.
 * Seu detalhamento para uso pode ser visto logo abaixo em *propriedades*.
 * Caso julgue necessÃ¡rio, pode-se tambÃ©m definir manualmente a rotina do componente.
 *
 *
 * Para a modal de digitaÃ§Ã£o de cÃ³digo SMS, Ã© possÃ­vel definir uma mensagem de erro
 * customizada com a propriedade `t-code-error` e hÃ¡ um link para
 * reenvio de cÃ³digo por SMS. Ao reenviar, o evento `t-code-submit` envia um objeto com o telefone do usuÃ¡rio e a quantidade
 * de vezes em que o usuÃ¡rio fez a solicitaÃ§Ã£o de reenvio.
 *
 * > Ã indicada a utilizaÃ§Ã£o da tela de digitaÃ§Ã£o para envio de cÃ³digo SMS apenas
 * se a opÃ§Ã£o por envio SMS for disponibilizada para o usuÃ¡rio.
 *
 *
 * A modal de confirmaÃ§Ã£o contÃ©m uma aÃ§Ã£o de reenvio e o evento `t-submit`
 * Ã© quem passa o objeto contendo o email em conjunto com a quantidade de tentativas de reenvio.
 *
 * > A tela de confirmaÃ§Ã£o Ã© indicada para quando o usuÃ¡rio solicitar a troca atravÃ©s do email.
 *
 * > Os textos das modals sÃ£o prÃ©-definidos, imutÃ¡veis e sÃ£o traduzidos de acordo com o idioma do *browser* (pt, en e es)
 *
 * Para que as imagens sejam exibidas corretamente, Ã© necessÃ¡rio incluir o caminho delas ao projeto. Para isso, edite
 * o *assets* no arquivo **angular.json** da aplicaÃ§Ã£o na seguinte ordem:
 * ```
 *   "assets": [
 *     "src/assets",
 *     "src/favicon.ico",
 *     {
 *       "glob": "**\/*",
 *       "input": "node_modules/@totvs/thf-theme/images",
 *       "output": "assets/images"
 *     }
 *   ]
 * ```
 */
export abstract class ThfModalPasswordRecoveryBaseComponent {

  private _contactEmail: string;
  private _phoneMask = ThfModalPasswordRecoveryDefaultPhone;
  private _type: ThfModalPasswordRecoveryType = ThfModalPasswordRecoveryTypeDefault;

  email: string;
  maxLength = ThfModalPasswordRecoveryDefaultMaxLength;
  minLength = ThfModalPasswordRecoveryDefaultMinLength;
  modalPasswordRecoveryTypeAll: boolean;
  phone: string;
  smsCode: string;
  smsCodeErrorMessage: string;

  literals: { cancelButton: string, closeButton: string, continueButton: string, email: string, emailErrorMessagePhrase: string,
    emailSentConfirmationPhrase: string, emailSentTitle: string, forgotPasswordTitle: string,
    insertCode: string, insertEmail: string, insertPhone: string, phoneErrorMessagePhrase: string,
    prepositionIn: string, prepositionOr: string, recoveryPasswordPhrase: string, resendEmailButton: string,
    resendSmsCodePhrase: string, sendAgain: string, sendButton: string, sms: string, smsCode: string,
    smsCodeErrorMessagePhrase: string, sentSmsCodePhrase: string, supportContact: string, telephone: string, typeCodeTitle: string } =
    {
      ...thfModalPasswordRecoveryLiterals[thfLocaleDefault],
      ...thfModalPasswordRecoveryLiterals[browserLanguage()],
    };

  /**
   * @optional
   *
   * @description
   *
   * DefiniÃ§Ã£o do e-mail que Ã© exibido na mensagem para contato de suporte.
   */
  @Input('t-contact-email') set contactEmail(value: string) {
    this._contactEmail = value;

    this.smsCodeErrorMessage = this.concatenateSMSErrorMessage(value);
  }

  get contactEmail() {
    return this._contactEmail;
  }

  /**
   * @optional
   *
   * @description
   *
   * DefiniÃ§Ã£o de mensagem de erro customizada para quando o usuÃ¡rio passar um cÃ³digo SMS invÃ¡lido ou errado.
   */
  @Input('t-code-error') codeError: string;

  /**
   * @optional
   *
   * @description
   *
   * DefiniÃ§Ã£o da mascara do campo de telefone.
   *
   * @default `(99) 99999-9999`
   */
  @Input('t-phone-mask') set phoneMask(value: string) {
    this._phoneMask = value || ThfModalPasswordRecoveryDefaultPhone;
    this.minLength = this.maxLength = this._phoneMask.length;
  }

  get phoneMask() {
    return this._phoneMask;
  }

  /**
   * @optional
   *
   * @description
   *
   * Define o tipo de recuperaÃ§Ã£o de senha que serÃ¡ exibido.
   *
   * @default `ThfModalPasswordRecoveryType.Email`
   *
   */
  @Input('t-type') set type(value: ThfModalPasswordRecoveryType) {
    this._type = (<any>Object).values(ThfModalPasswordRecoveryType).includes(value) ? value : ThfModalPasswordRecoveryTypeDefault;
  }

  get type() {
    return this._type;
  }

  /**
   * @optional
   *
   * @description
   *
   * Endpoint usado pelo template para requisiÃ§Ã£o do recurso. Quando preenchido,
   * o mÃ©todos `t-submit` e `t-submit-code` serÃ£o ignorados e o componente adquirirÃ¡ automatizaÃ§Ã£o
   * para o processo de solicitaÃ§Ã£o de troca de senha.
   *
   * ### Processos
   * Ao digitar um valor vÃ¡lido no campo de email/telefone e pressionar **enviar**,
   * o componente farÃ¡ uma requisiÃ§Ã£o `POST` na url especificada nesta propriedade passando o objeto contendo o valor definido pelo usuÃ¡rio.
   *
   * ```
   * body {
   *  email: email,
   *  retry?: retry
   * }
   * ```
   *
   *
   * #### RecuperaÃ§Ã£o por email
   * Para a recuperaÃ§Ã£o de senha por **email**, o cÃ³digo de resposta HTTP de status esperado Ã© `204`.
   *
   * Em caso de **sucesso**, serÃ¡ exibida a modal de confirmaÃ§Ã£o de e-mail para o usuÃ¡rio.
   *
   *
   * > A aÃ§Ã£o **Reenviar** na tela de confirmaÃ§Ã£o efetua uma nova requisiÃ§Ã£o
   * passando-se o objeto com incremento para o valor da propriedade **retry**.
   *
   * *Processo finalizado.*
   *
   *
   * #### RecuperaÃ§Ã£o por SMS
   * Se a opÃ§Ã£o de recuperaÃ§Ã£o for por **SMS**, o cÃ³digo de status de sucesso deve ser `200`.
   * Em caso de **sucesso**, abre-se a modal de digitaÃ§Ã£o de cÃ³digo SMS e a resposta
   * desta requisiÃ§Ã£o deve retornar uma definiÃ§Ã£o de dados abaixo:
   *
   * ```
   * 200:
   *  {
   *    hash: hash,
   *    urlValidationCode?: url
   *  }
   * ```
   *
   *
   * - O **hash** serÃ¡ o cÃ³digo de validaÃ§Ã£o da solicitaÃ§Ã£o do SMS para ser enviado juntamente com o cÃ³digo de verificaÃ§Ã£o do SMS;
   * - **urlValidationCode** Ã© a url usada para validaÃ§Ã£o do cÃ³digo enviado por SMS.
   *
   *
   * > Caso nÃ£o seja passado urlValidationCode, o endpoint usado para validaÃ§Ã£o do cÃ³digo serÃ¡ `<t-url-recovery>/validation`.
   *
   *
   * #### ValidaÃ§Ã£o do cÃ³digo SMS
   * Ao digitar um valor vÃ¡lido no campo de cÃ³digo SMS e pressionar **continuar**, o componente farÃ¡ uma requisiÃ§Ã£o `POST` contendo:
   *
   * ```
   * POST /<t-url-recovery>/validation OU /<urlValidationCode>
   * Body {
   *  hash: hash,
   *  code: code
   * }
   * ```
   *
   *
   * O cÃ³digo de resposta HTTP de status esperado Ã© `200`.
   *
   * Em caso de **erro** na validaÃ§Ã£o do cÃ³digo SMS, a modal se mantÃ©m com o campo para digitaÃ§Ã£o
   * de cÃ³digo SMS
   *
   *
   * > Pode-se atribuir a mensagem de erro (message) para o atributo `t-code-error` conforme retorno abaixo:
   *
   * ```
   * 400
   *  {
   *    error {
   *      message: 'Error Message'
   *    }
   *  }
   * ```
   *
   *
   * Em caso de **sucesso**, espera-se a resposta desta requisiÃ§Ã£o retornando a seguinte definiÃ§Ã£o:
   *
   * ```
   * 200:
   *  {
   *    token: token,
   *    urlChangePassword?: url
   *  }
   * ```
   *
   *
   * - **token**: Token de alteraÃ§Ã£o de senha;
   * - **urlChangePassword**: url para o formulÃ¡rio de alteraÃ§Ã£o de senha.
   *
   *
   * O componente estÃ¡ configurado para redirecionar para a url estabelecida em `urlChangePassword`.
   *
   * > Caso nÃ£o seja passado valor para urlChangePassword,
   * a url usada para validaÃ§Ã£o serÃ¡ a `<t-url-recovery>/changePassword?token=<token>`.
   *
   * *Processo finalizado.*
   */
  @Input('t-url-recovery') urlRecovery?: string;

  /**
   * @optional
   *
   * @description
   *
   * AÃ§Ã£o contendo como parÃ¢metro o cÃ³digo enviado por SMS e digitado pelo usuÃ¡rio.
   *
   * > Esta propriedade serÃ¡ ignorada se for definido valor para a propriedade `t-url-recovery`.
   */
  @Output('t-code-submit') codeSubmit = new EventEmitter<any>();

  /**
   * @optional
   *
   * @description
   *
   * AÃ§Ã£o contendo o email como parÃ¢metro e que Ã© executada quando o usuÃ¡rio clica sobres os botÃµes de 'enviar' e 'reenviar' e-mail.
   *
   * > Esta propriedade serÃ¡ ignorada se for definido valor para a propriedade `t-url-recovery`.
   */
  @Output('t-submit') submit = new EventEmitter<any>();

  /**
   * AcÃ£o para conclusÃ£o de processo e fechamento da modal. Indica-se sua utilizaÃ§Ã£o
   * para apÃ³s o envio e validaÃ§Ã£o do cÃ³digo SMS enviado pelo usuÃ¡rio.
   *
   * > Nas modals em que hÃ¡ a aÃ§Ã£o de 'cancelar' dispensa-se o uso desta aÃ§Ã£o pois o componente jÃ¡ trata o fechamento da modal.
   */
  abstract completed(): void;

  /**
   * Abre a modal de preenchimento de email ou nÃºmero de telefone para solicitaÃ§Ã£o de troca de senha.
   */
  abstract open(): void;

  /**
   * Abre a modal de confirmaÃ§Ã£o de envio de email.
   */
  abstract openConfirmation(): void;

  /**
   * Abre a modal de preenchimento do cÃ³digo SMS enviado ao usuÃ¡rio.
   */
  abstract openSmsCode(): void;

  private concatenateSMSErrorMessage(value: string) {
    const literalCodeErrorMessage = this.literals.smsCodeErrorMessagePhrase;

    return value && value !== '' ? `${literalCodeErrorMessage} ${this.literals.prepositionIn} ${value}.` : literalCodeErrorMessage;
  }

}
